import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Check, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface SubaccountSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountData: {
    account_name: string;
    email: string;
    temporary_password: string;
  };
  emailSent: boolean;
  emailError?: string;
  onResendEmail?: () => void;
}

const SubaccountSuccessModal: React.FC<SubaccountSuccessModalProps> = ({
  isOpen,
  onClose,
  accountData,
  emailSent,
  emailError,
  onResendEmail,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(accountData.temporary_password);
      setCopied(true);
      toast.success("Senha copiada para a área de transferência");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erro ao copiar senha");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex w-full max-w-lg p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
        <div className="p-6">
          {/* Header com ícone de sucesso */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-semibold text-neutral-50 mb-2">
              Conta criada com sucesso!
            </h2>
            <p className="text-neutral-300 text-sm">
              A subconta foi configurada e o usuário foi criado no sistema
            </p>
          </div>

          {/* Informações da conta */}
          <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-4 mb-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">
                  Nome da Conta
                </p>
                <p className="text-neutral-50 font-medium">{accountData.account_name}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">
                  Email do Usuário
                </p>
                <p className="text-neutral-50 font-medium">{accountData.email}</p>
              </div>
            </div>
          </div>

          {/* Senha temporária */}
          <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 border-2 border-neutral-600 rounded-lg p-6 mb-4">
            <div className="text-center">
              <p className="text-xs text-neutral-400 uppercase tracking-widest mb-3">
                Senha Temporária
              </p>
              <div className="bg-neutral-950 border border-neutral-700 rounded-md px-4 py-3 mb-4">
                <p className="text-2xl font-mono font-bold text-neutral-50 tracking-wider select-all">
                  {accountData.temporary_password}
                </p>
              </div>
              <Button
                onClick={handleCopyPassword}
                className="w-full bg-neutral-700 hover:bg-neutral-600 text-white"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Senha
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Status do email */}
          {emailSent ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-green-400 font-medium text-sm mb-1">
                    Email enviado com sucesso
                  </p>
                  <p className="text-green-300/80 text-xs">
                    O usuário receberá um email de boas-vindas com as credenciais de acesso
                  </p>
                </div>
              </div>
            </div>
          ) : emailError ? (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-orange-400 font-medium text-sm mb-1">
                    Email não foi enviado
                  </p>
                  <p className="text-orange-300/80 text-xs mb-3">
                    {emailError || "Ocorreu um erro ao enviar o email de boas-vindas"}
                  </p>
                  {onResendEmail && (
                    <Button
                      onClick={onResendEmail}
                      size="sm"
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <Mail className="w-3 h-3 mr-2" />
                      Reenviar Email
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Avisos importantes */}
          <div className="bg-neutral-800/30 border border-neutral-700 rounded-lg p-4 mb-6">
            <p className="text-xs text-neutral-400 uppercase tracking-wider mb-2">
              Importante
            </p>
            <ul className="space-y-2 text-sm text-neutral-300">
              <li className="flex items-start gap-2">
                <span className="text-neutral-500 mt-1">•</span>
                <span>Esta senha não será mostrada novamente. Guarde em local seguro.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neutral-500 mt-1">•</span>
                <span>O usuário deve alterar a senha no primeiro acesso.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neutral-500 mt-1">•</span>
                <span>As credenciais foram enviadas por email para o usuário.</span>
              </li>
            </ul>
          </div>

          {/* Botão fechar */}
          <Button
            onClick={onClose}
            className="w-full bg-[#E4E4E7] text-black hover:bg-[#D4D4D8]"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubaccountSuccessModal;
